import {
  Button,
  MessageBanner,
  ArrowIcon,
  TextArea,
} from "@olivahealth/oli-ui";
import { useForm } from "react-hook-form";
import tokens from "@olivahealth/oli-ui/theme/tokens";
import React, { type ReactElement } from "react";
import { useRouter } from "next/router";
import { CREATE_THREAD_TRACK_FROM } from "@olivahealth/constants";
import cx from "classnames";
import useTranslation from "../../../hooks/useTranslation";
import { useUserData } from "../../../services/contexts/UserDataContext";
import { useChatContext } from "../../../services/contexts/ChatContext";
import * as s from "./styles";

interface FormValues {
  message: string;
}

export function CompanionWidget({
  variant = "textarea",
  hasThreads,
  hasWellbeingProfile,
  from = "default",
  careModality,
  threadType,
  placeholder,
}: {
  variant?: "textarea" | "button";
  hasThreads?: boolean;
  hasWellbeingProfile?: boolean;
  from: "default" | "oliva_at_work";
  careModality?: "THERAPY" | "PROFESSIONAL_COACHING";
  threadType?: "WORK" | "WELLBEING";
  placeholder?: string;
}): ReactElement {
  const { t } = useTranslation("companion");
  const { data: user } = useUserData();
  const { createCompanionThread, isCreatingThread } = useChatContext();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
    setError,
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: {
      message: "",
    },
    mode: "onChange",
  });

  const isButtonDisabled = !isDirty || isCreatingThread;
  const errorMessage = errors.message?.message;
  const isOlivaAtWork = from === "oliva_at_work";

  const clearErrorMessage = () => {
    if (!errorMessage) {
      return;
    }
    clearErrors("message");
  };

  const createEmptyThread = () => {
    onSubmit(
      { message: "" },
      isOlivaAtWork
        ? CREATE_THREAD_TRACK_FROM.OLIVA_AT_WORK
        : CREATE_THREAD_TRACK_FROM.HOME,
    );
  };

  const handleStartWithOli = () => {
    if (hasWellbeingProfile) {
      router.push("/wellbeing/insights");
    } else {
      router.push("/wellbeing");
    }
  };

  const onSubmit = (
    data: FormValues,
    from: (typeof CREATE_THREAD_TRACK_FROM)[keyof typeof CREATE_THREAD_TRACK_FROM],
  ) => {
    if (!user?.id) {
      return;
    }

    createCompanionThread(
      data.message,
      from,
      () => {
        setError("message", {
          type: "manual",
          message: t("form.error.generic"),
        });
      },
      careModality,
      threadType,
    );
    clearErrors("message");
    reset();
  };

  if (!hasThreads) {
    return (
      <div className={s.container}>
        <div className={s.formContainer}>
          <div className="flex justify-center mb-6">
            <Button
              onClick={handleStartWithOli}
              disabled={isCreatingThread}
              variant="green"
              width="full"
            >
              {t("form.buttons.startWithOli")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={s.container}>
      <div className={s.formContainer}>
        {variant === "textarea" ? (
          <form
            className="w-full max-w-3xl"
            onSubmit={handleSubmit((data) =>
              onSubmit(
                data,
                isOlivaAtWork
                  ? CREATE_THREAD_TRACK_FROM.OLIVA_AT_WORK
                  : CREATE_THREAD_TRACK_FROM.HOME,
              ),
            )}
          >
            <div className="relative w-full">
              <TextArea
                id="message"
                variant="dark"
                minRows={isOlivaAtWork ? 4 : 1.5}
                placeholder={placeholder || t("form.placeholder")}
                register={register}
                validationOptions={{ required: true }}
                onChange={clearErrorMessage}
                hasBottomContent
                resizable={false}
                hasOutline
              />
              {errorMessage ? (
                <MessageBanner
                  variant="error"
                  isOpen
                  message={errorMessage}
                  showCloseButton={false}
                />
              ) : null}
              <button
                disabled={isButtonDisabled}
                type="submit"
                className={cx(s.submitButton, {
                  [s.submitButtonDisabled]: isButtonDisabled,
                })}
              >
                <ArrowIcon direction="up" color={tokens.colors.neutral[900]} />
              </button>
            </div>
          </form>
        ) : (
          <div className="flex justify-center mb-6">
            <Button
              onClick={createEmptyThread}
              disabled={isCreatingThread}
              variant="green"
              width="full"
            >
              {t("form.buttons.start")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
